/**
 * Dataverse depth tools — FetchXML, relevance search, choice/relationship
 * metadata, upsert/assign/associate, schema write, and $batch.
 *
 * Design notes:
 * - The metadata tools exist to STOP schema hallucination: option-set
 *   label↔value mapping and relationship names are the two things an AI
 *   cannot guess correctly, and getting them wrong produces silent bad
 *   writes (wrong statuscode) or OData 404s.
 * - Errors from these tools carry a category prefix — PERMISSIONS,
 *   SCHEMA_MISMATCH, or ENV_LIMITATION — so the AI knows whether to fix a
 *   name, ask an admin, or stop retrying something the environment cannot do.
 * - Schema writes and batch deletes are confirm-gated like every other
 *   destructive operation in this server.
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { DataverseApi } from "../api/dataverse-api.js";
import { ApiError, ConfirmationRequiredError } from "../utils/errors.js";
import { logger } from "../utils/logger.js";

// =============================================================================
// Error categories
// =============================================================================

/**
 * Classify a Dataverse failure so the model knows what KIND of problem it is:
 * - PERMISSIONS      — the signed-in user (or app consent) lacks a privilege
 * - SCHEMA_MISMATCH  — a table/column/relationship name doesn't exist
 * - ENV_LIMITATION   — the environment has the feature disabled
 * Unknown failures pass through unchanged.
 */
export function categorizeDataverseError(err: unknown): unknown {
  if (!(err instanceof ApiError)) return err;
  const text = `${err.message} ${typeof err.details === "string" ? err.details : ""}`;
  const status = err.statusCode ?? 500;

  if (/relevance search|dataverse search.*(disabled|not enabled)|IsExternalSearchIndexEnabled/i.test(text)) {
    return new ApiError(
      `[ENV_LIMITATION] ${err.message} — Dataverse (relevance) search is not enabled in this environment. ` +
        "An admin can turn it on in the Power Platform admin center under Environment > Settings > Features. Do not retry until then.",
      status,
      err.details,
    );
  }
  if (err.statusCode === 401 || err.statusCode === 403) {
    return new ApiError(
      `[PERMISSIONS] ${err.message} — the signed-in user lacks a privilege for this operation ` +
        "(or the operation needs a security role like System Customizer for schema changes). Ask an admin; retrying will not help.",
      status,
      err.details,
    );
  }
  if (
    (err.statusCode === 400 || err.statusCode === 404) &&
    /could not find|does not exist|not found|no property|invalid (attribute|entity|property|relationship)|undeclared property/i.test(text)
  ) {
    return new ApiError(
      `[SCHEMA_MISMATCH] ${err.message} — a table, column, or relationship name doesn't match this environment's schema. ` +
        "Ground the names first: list_dataverse_tables / get_dataverse_table for columns, get_dataverse_relationships for " +
        "relationship names, get_dataverse_option_set for choice values.",
      status,
      err.details,
    );
  }
  return err;
}

async function withCategory<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw categorizeDataverseError(err);
  }
}

// =============================================================================
// Tool Definitions
// =============================================================================

export const executeFetchXmlTool = {
  name: "execute_fetchxml",
  description:
    "Run a FetchXML query against Dataverse — use this instead of query_dataverse_rows when you need aggregates " +
    "(count, sum, avg, min, max), grouping, or joins across related tables (link-entity). " +
    "Example: total opportunities by owner, cases grouped by status. Pass the entity set name and the full <fetch> XML.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name being queried (e.g., 'opportunities'). Get it from list_dataverse_tables.",
      },
      fetchXml: {
        type: "string",
        description:
          "Complete FetchXML document, e.g. <fetch aggregate='true'><entity name='opportunity'><attribute name='estimatedvalue' alias='total' aggregate='sum'/></entity></fetch>",
      },
    },
    required: ["entitySetName", "fetchXml"],
  },
};

export const searchDataverseTool = {
  name: "search_dataverse",
  description:
    "Full-text search across Dataverse tables (relevance search) — 'find anything mentioning Contoso'. " +
    "Returns ranked matches with the table and record id for follow-up reads. " +
    "Requires the environment's Dataverse search feature to be enabled (an ENV_LIMITATION error means it isn't).",
  inputSchema: {
    type: "object" as const,
    properties: {
      search: { type: "string", description: "The search text." },
      entities: {
        type: "array",
        items: { type: "string" },
        description: "Optional table logical names to restrict the search (e.g., ['account','contact']).",
      },
      top: { type: "number", minimum: 1, maximum: 100, description: "Max results (default 25)." },
    },
    required: ["search"],
  },
};

export const getDataverseOptionSetTool = {
  name: "get_dataverse_option_set",
  description:
    "Get the labels and integer values of a choice (option set) column or a global option set. " +
    "ALWAYS use this before writing to a choice column (statuscode, statecode, custom picklists) — " +
    "the integer values cannot be guessed, and a wrong value writes bad data silently. " +
    "Pass table+column for a column's choices, or globalName for a shared option set.",
  inputSchema: {
    type: "object" as const,
    properties: {
      tableLogicalName: { type: "string", description: "Table logical name (e.g., 'account') — with columnLogicalName." },
      columnLogicalName: { type: "string", description: "Choice column logical name (e.g., 'statuscode')." },
      globalName: { type: "string", description: "Global option set name — instead of table+column." },
    },
  },
};

export const getDataverseRelationshipsTool = {
  name: "get_dataverse_relationships",
  description:
    "List a table's relationships (1:N, N:1, N:N) with their schema names and navigation properties. " +
    "Use before associate_dataverse_rows / disassociate_dataverse_rows or $expand queries — relationship " +
    "names cannot be guessed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      logicalName: { type: "string", description: "Table logical name (e.g., 'account')." },
      relationshipType: {
        type: "string",
        enum: ["oneToMany", "manyToOne", "manyToMany", "all"],
        description: "Filter by relationship type (default all).",
      },
    },
    required: ["logicalName"],
  },
};

export const upsertDataverseRowTool = {
  name: "upsert_dataverse_row",
  description:
    "Create-or-update a Dataverse row addressed by alternate key(s) instead of a GUID — ideal for syncing " +
    "external data (e.g., rows keyed by an account number). Reports whether the row was created or updated.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: { type: "string", description: "The entity set name (e.g., 'accounts')." },
      keys: {
        type: "object",
        description: "Alternate key column(s) and value(s), e.g. {\"accountnumber\": \"A-1042\"}. The table must define this alternate key.",
        additionalProperties: { type: "string" },
      },
      data: {
        type: "object",
        description: "Column values to set (same shape as create_dataverse_row).",
      },
    },
    required: ["entitySetName", "keys", "data"],
  },
};

export const assignDataverseRowTool = {
  name: "assign_dataverse_row",
  description:
    "Change the owner of a Dataverse row to another user or team. Use list-type tools or search to find the new owner's id first.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: { type: "string", description: "The entity set name (e.g., 'opportunities')." },
      id: { type: "string", description: "Row GUID." },
      ownerType: { type: "string", enum: ["user", "team"], description: "Whether the new owner is a user or a team." },
      ownerId: { type: "string", description: "GUID of the systemuser or team taking ownership." },
    },
    required: ["entitySetName", "id", "ownerType", "ownerId"],
  },
};

export const associateDataverseRowsTool = {
  name: "associate_dataverse_rows",
  description:
    "Link two existing Dataverse rows through a named relationship (N:N or 1:N) — e.g., add a contact to a " +
    "marketing list. Get the relationship's navigation property name from get_dataverse_relationships first.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: { type: "string", description: "Source entity set name." },
      id: { type: "string", description: "Source row GUID." },
      relationshipName: {
        type: "string",
        description: "Relationship navigation property (from get_dataverse_relationships).",
      },
      relatedEntitySetName: { type: "string", description: "Related entity set name." },
      relatedId: { type: "string", description: "Related row GUID." },
    },
    required: ["entitySetName", "id", "relationshipName", "relatedEntitySetName", "relatedId"],
  },
};

export const disassociateDataverseRowsTool = {
  name: "disassociate_dataverse_rows",
  description:
    "Remove a relationship link between two Dataverse rows (the rows themselves are untouched). " +
    "Set confirm=true to proceed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: { type: "string", description: "Source entity set name." },
      id: { type: "string", description: "Source row GUID." },
      relationshipName: { type: "string", description: "Relationship navigation property." },
      relatedId: {
        type: "string",
        description: "Related row GUID (required for N:N / collection-valued relationships; omit for single-valued lookups).",
      },
      confirm: { type: "boolean", description: "Must be true to remove the link." },
    },
    required: ["entitySetName", "id", "relationshipName"],
  },
};

export const createDataverseColumnTool = {
  name: "create_dataverse_column",
  description:
    "Create a new column on a Dataverse table. Supports string, memo, integer, decimal, money, datetime, " +
    "boolean, and picklist (choice) types. The schema name must carry your solution publisher's prefix " +
    "(e.g., 'new_ProjectCode'). Requires confirm=true. After creating, run publish_all_customizations for the " +
    "column to appear in apps.",
  inputSchema: {
    type: "object" as const,
    properties: {
      tableLogicalName: { type: "string", description: "Table logical name (e.g., 'account')." },
      schemaName: { type: "string", description: "Column schema name WITH publisher prefix, e.g. 'new_ProjectCode'." },
      displayName: { type: "string", description: "Human-readable column label." },
      description: { type: "string", description: "Optional column description." },
      type: {
        type: "string",
        enum: ["string", "memo", "integer", "decimal", "money", "datetime", "boolean", "picklist"],
        description: "Column data type.",
      },
      required: { type: "boolean", description: "Whether the column is business-required (default false)." },
      maxLength: { type: "number", description: "string/memo only: max length (default 100 / 2000)." },
      dateTimeBehavior: {
        type: "string",
        enum: ["UserLocal", "DateOnly", "TimeZoneIndependent"],
        description: "datetime only (default UserLocal).",
      },
      options: {
        type: "array",
        items: {
          type: "object",
          properties: {
            value: { type: "number", description: "Integer value (use publisher option value prefix conventions)." },
            label: { type: "string", description: "Display label." },
          },
          required: ["value", "label"],
        },
        description: "picklist only: the choices.",
      },
      confirm: { type: "boolean", description: "Must be true to create the column." },
    },
    required: ["tableLogicalName", "schemaName", "displayName", "type"],
  },
};

export const createDataverseLookupColumnTool = {
  name: "create_dataverse_lookup_column",
  description:
    "Create a lookup (N:1 foreign-key) column on a table, pointing at another table — creates the column and " +
    "the relationship together, which is the only way the Web API supports it. Requires confirm=true.",
  inputSchema: {
    type: "object" as const,
    properties: {
      tableLogicalName: { type: "string", description: "Table that GETS the lookup column (the many side), e.g. 'new_project'." },
      targetTableLogicalName: { type: "string", description: "Table the lookup points AT (the one side), e.g. 'account'." },
      schemaName: { type: "string", description: "Lookup column schema name WITH publisher prefix, e.g. 'new_Customer'." },
      displayName: { type: "string", description: "Human-readable label for the lookup column." },
      relationshipSchemaName: {
        type: "string",
        description: "Schema name for the relationship, e.g. 'new_account_project_customer'. Defaults to '<schemaName>_relationship'.",
      },
      confirm: { type: "boolean", description: "Must be true to create the column and relationship." },
    },
    required: ["tableLogicalName", "targetTableLogicalName", "schemaName", "displayName"],
  },
};

export const batchDataverseOperationsTool = {
  name: "batch_dataverse_operations",
  description:
    "Execute up to 100 Dataverse operations in one request — the right tool for bulk work like 'add every " +
    "approved row from this spreadsheet'. Set atomic=true to make it all-or-nothing (any failure rolls back " +
    "everything; GETs not allowed in atomic mode). Batches containing DELETE operations require confirm=true.",
  inputSchema: {
    type: "object" as const,
    properties: {
      operations: {
        type: "array",
        items: {
          type: "object",
          properties: {
            method: { type: "string", enum: ["GET", "POST", "PATCH", "DELETE"] },
            entitySetName: { type: "string", description: "Entity set name for this operation." },
            id: { type: "string", description: "Row GUID (PATCH/DELETE/GET of one row)." },
            data: { type: "object", description: "Body for POST/PATCH." },
          },
          required: ["method", "entitySetName"],
        },
        description: "The operations, executed in order.",
      },
      atomic: { type: "boolean", description: "All-or-nothing changeset (default false)." },
      confirm: { type: "boolean", description: "Required when any operation is a DELETE." },
    },
    required: ["operations"],
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const executeFetchXmlInputSchema = z.object({
  entitySetName: z.string().min(1),
  fetchXml: z.string().min(10).max(100_000),
});

export const searchDataverseInputSchema = z.object({
  search: z.string().min(1).max(1000),
  entities: z.array(z.string().min(1)).max(20).optional(),
  top: z.number().min(1).max(100).optional(),
});

export const getDataverseOptionSetInputSchema = z
  .object({
    tableLogicalName: z.string().min(1).optional(),
    columnLogicalName: z.string().min(1).optional(),
    globalName: z.string().min(1).optional(),
  })
  .refine(
    (v) => Boolean(v.globalName) !== Boolean(v.tableLogicalName && v.columnLogicalName),
    { message: "Pass either globalName, or tableLogicalName + columnLogicalName (not both)." },
  );

export const getDataverseRelationshipsInputSchema = z.object({
  logicalName: z.string().min(1),
  relationshipType: z.enum(["oneToMany", "manyToOne", "manyToMany", "all"]).optional(),
});

export const upsertDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  keys: z.record(z.string(), z.string()).refine((k) => Object.keys(k).length >= 1 && Object.keys(k).length <= 5, {
    message: "Provide 1-5 alternate key columns.",
  }),
  data: z.record(z.string(), z.unknown()),
});

export const assignDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  ownerType: z.enum(["user", "team"]),
  ownerId: z.string().min(1),
});

export const associateDataverseRowsInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  relationshipName: z.string().min(1),
  relatedEntitySetName: z.string().min(1),
  relatedId: z.string().min(1),
});

export const disassociateDataverseRowsInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  relationshipName: z.string().min(1),
  relatedId: z.string().min(1).optional(),
  confirm: z.boolean().optional(),
});

const SCHEMA_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{1,99}$/;

export const createDataverseColumnInputSchema = z.object({
  tableLogicalName: z.string().min(1),
  schemaName: z.string().regex(SCHEMA_NAME_RE, "Schema name must be alphanumeric/underscore, starting with a letter."),
  displayName: z.string().min(1).max(100),
  description: z.string().max(1000).optional(),
  type: z.enum(["string", "memo", "integer", "decimal", "money", "datetime", "boolean", "picklist"]),
  required: z.boolean().optional(),
  maxLength: z.number().min(1).max(1_048_576).optional(),
  dateTimeBehavior: z.enum(["UserLocal", "DateOnly", "TimeZoneIndependent"]).optional(),
  options: z.array(z.object({ value: z.number().int(), label: z.string().min(1).max(200) })).max(300).optional(),
  confirm: z.boolean().optional(),
});

export const createDataverseLookupColumnInputSchema = z.object({
  tableLogicalName: z.string().min(1),
  targetTableLogicalName: z.string().min(1),
  schemaName: z.string().regex(SCHEMA_NAME_RE, "Schema name must be alphanumeric/underscore, starting with a letter."),
  displayName: z.string().min(1).max(100),
  relationshipSchemaName: z.string().regex(SCHEMA_NAME_RE).optional(),
  confirm: z.boolean().optional(),
});

export const batchDataverseOperationsInputSchema = z.object({
  operations: z
    .array(
      z.object({
        method: z.enum(["GET", "POST", "PATCH", "DELETE"]),
        entitySetName: z.string().min(1),
        id: z.string().optional(),
        data: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .min(1)
    .max(100),
  atomic: z.boolean().optional(),
  confirm: z.boolean().optional(),
});

// =============================================================================
// Handlers
// =============================================================================

export interface DataverseDepthToolsContext {
  api: DataverseApi | null;
}

function notAvailable(): string {
  return "Dataverse not available for this environment. Create or select an environment with a Dataverse database.";
}

function label(l: unknown): string {
  if (l && typeof l === "object") {
    const user = (l as Record<string, unknown>)["UserLocalizedLabel"];
    if (user && typeof user === "object") {
      const text = (user as Record<string, unknown>)["Label"];
      if (typeof text === "string") return text;
    }
  }
  return "";
}

export async function handleExecuteFetchXml(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = executeFetchXmlInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName }, "Executing execute_fetchxml");

  const result = await withCategory(() => context.api!.executeFetchXml(parsed.entitySetName, parsed.fetchXml));
  if (result.value.length === 0) return "FetchXML query returned no rows.";

  const lines = [`FetchXML results (${result.value.length} rows):`];
  for (let i = 0; i < result.value.length; i++) {
    const row = result.value[i];
    if (!row) continue;
    lines.push(`\n  Row ${i + 1}:`);
    for (const [key, value] of Object.entries(row)) {
      if (key.startsWith("@odata")) continue;
      if (value === null || value === undefined) continue;
      lines.push(`    ${key}: ${displayValue(value)}`);
    }
  }
  return lines.join("\n");
}

export async function handleSearchDataverse(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = searchDataverseInputSchema.parse(input);
  logger.info({}, "Executing search_dataverse");

  const result = await withCategory(() => context.api!.searchRows(parsed.search, parsed.entities, parsed.top));
  if (!result.value || result.value.length === 0) {
    return `No matches for "${parsed.search}".`;
  }

  const lines = [`Search results for "${parsed.search}" (${result.value.length} matches):`];
  for (const hit of result.value) {
    const entity = displayValue(hit["@search.entityname"] ?? hit["entityname"] ?? "?");
    const id = displayValue(hit["@search.objectid"] ?? hit["objectid"] ?? "?");
    const score = hit["@search.score"];
    const highlights = hit["@search.highlights"];
    lines.push(`\n  ${entity} (${id})${typeof score === "number" ? ` — score ${score.toFixed(2)}` : ""}`);
    if (highlights && typeof highlights === "object") {
      for (const [field, frags] of Object.entries(highlights as Record<string, unknown>)) {
        lines.push(`    ${field}: ${displayValue(frags)}`);
      }
    }
  }
  return lines.join("\n");
}

export async function handleGetDataverseOptionSet(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = getDataverseOptionSetInputSchema.parse(input);

  if (parsed.globalName) {
    logger.info({ globalName: parsed.globalName }, "Executing get_dataverse_option_set (global)");
    const meta = await withCategory(() => context.api!.getGlobalOptionSet(parsed.globalName!));
    return formatOptionSet(`Global option set '${parsed.globalName}'`, meta);
  }

  logger.info(
    { table: parsed.tableLogicalName, column: parsed.columnLogicalName },
    "Executing get_dataverse_option_set (column)",
  );
  const { cast, metadata } = await withCategory(() =>
    context.api!.getAttributeOptionSet(parsed.tableLogicalName!, parsed.columnLogicalName!),
  );
  const optionSet =
    (metadata["OptionSet"] as Record<string, unknown> | null) ??
    (metadata["GlobalOptionSet"] as Record<string, unknown> | null);
  const header = `Choices for ${parsed.tableLogicalName}.${parsed.columnLogicalName} (${cast.replace("AttributeMetadata", "")})`;
  if (!optionSet) return `${header}: no option set metadata returned.`;
  return formatOptionSet(header, optionSet);
}

function formatOptionSet(header: string, meta: Record<string, unknown>): string {
  const lines = [`${header}:`];
  const options = meta["Options"];
  if (Array.isArray(options) && options.length > 0) {
    for (const opt of options as Array<Record<string, unknown>>) {
      lines.push(`  ${displayValue(opt["Value"])} = ${label(opt["Label"]) || "(no label)"}`);
    }
    return lines.join("\n");
  }
  // Boolean columns: TrueOption / FalseOption instead of Options[]
  const trueOpt = meta["TrueOption"] as Record<string, unknown> | undefined;
  const falseOpt = meta["FalseOption"] as Record<string, unknown> | undefined;
  if (trueOpt || falseOpt) {
    if (trueOpt) lines.push(`  ${displayValue(trueOpt["Value"])} = ${label(trueOpt["Label"])} (true)`);
    if (falseOpt) lines.push(`  ${displayValue(falseOpt["Value"])} = ${label(falseOpt["Label"])} (false)`);
    return lines.join("\n");
  }
  return `${header}: option set has no options.`;
}

export async function handleGetDataverseRelationships(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = getDataverseRelationshipsInputSchema.parse(input);
  logger.info({ logicalName: parsed.logicalName }, "Executing get_dataverse_relationships");

  const meta = await withCategory(() => context.api!.getRelationships(parsed.logicalName));
  const type = parsed.relationshipType ?? "all";
  const lines = [`Relationships for '${parsed.logicalName}':`];

  const section = (title: string, key: string, render: (r: Record<string, unknown>) => string): void => {
    const rels = meta[key];
    if (!Array.isArray(rels) || rels.length === 0) return;
    lines.push(`\n  ${title} (${rels.length}):`);
    for (const rel of rels as Array<Record<string, unknown>>) {
      lines.push(`    ${render(rel)}`);
    }
  };

  if (type === "all" || type === "oneToMany") {
    section("1:N (this table is the ONE side)", "OneToManyRelationships", (r) =>
      `${displayValue(r["SchemaName"])} → ${displayValue(r["ReferencingEntity"])}.${displayValue(r["ReferencingAttribute"])} (nav: ${displayValue(r["ReferencedEntityNavigationPropertyName"])})`,
    );
  }
  if (type === "all" || type === "manyToOne") {
    section("N:1 (lookups on this table)", "ManyToOneRelationships", (r) =>
      `${displayValue(r["SchemaName"])} → ${displayValue(r["ReferencedEntity"])} via ${displayValue(r["ReferencingAttribute"])} (nav: ${displayValue(r["ReferencingEntityNavigationPropertyName"])})`,
    );
  }
  if (type === "all" || type === "manyToMany") {
    section("N:N", "ManyToManyRelationships", (r) =>
      `${displayValue(r["SchemaName"])}: ${displayValue(r["Entity1LogicalName"])} ↔ ${displayValue(r["Entity2LogicalName"])} (intersect: ${displayValue(r["IntersectEntityName"])})`,
    );
  }

  if (lines.length === 1) return `No relationships found for '${parsed.logicalName}' (type filter: ${type}).`;
  return lines.join("\n");
}

export async function handleUpsertDataverseRow(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = upsertDataverseRowInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName }, "Executing upsert_dataverse_row");

  const { outcome } = await withCategory(() =>
    context.api!.upsertRow(parsed.entitySetName, parsed.keys, parsed.data),
  );
  const keyDesc = Object.entries(parsed.keys)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ");
  return `${outcome === "created" ? "Created" : "Updated"} row in ${parsed.entitySetName} (key: ${keyDesc}).\nFields set: ${Object.keys(parsed.data).join(", ")}`;
}

export async function handleAssignDataverseRow(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = assignDataverseRowInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName, id: parsed.id }, "Executing assign_dataverse_row");

  await withCategory(() =>
    context.api!.assignRow(parsed.entitySetName, parsed.id, parsed.ownerType, parsed.ownerId),
  );
  return `Assigned ${parsed.entitySetName} row ${parsed.id} to ${parsed.ownerType} ${parsed.ownerId}.`;
}

export async function handleAssociateDataverseRows(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = associateDataverseRowsInputSchema.parse(input);
  logger.info({ relationship: parsed.relationshipName }, "Executing associate_dataverse_rows");

  await withCategory(() =>
    context.api!.associateReference(
      parsed.entitySetName,
      parsed.id,
      parsed.relationshipName,
      parsed.relatedEntitySetName,
      parsed.relatedId,
    ),
  );
  return `Linked ${parsed.entitySetName}(${parsed.id}) ↔ ${parsed.relatedEntitySetName}(${parsed.relatedId}) via '${parsed.relationshipName}'.`;
}

export async function handleDisassociateDataverseRows(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = disassociateDataverseRowsInputSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Not confirmed. Set confirm=true to remove the '${parsed.relationshipName}' link on ${parsed.entitySetName}(${parsed.id}). The rows themselves are not deleted.`);
  }
  logger.info({ relationship: parsed.relationshipName }, "Executing disassociate_dataverse_rows");

  await withCategory(() =>
    context.api!.disassociateReference(parsed.entitySetName, parsed.id, parsed.relationshipName, parsed.relatedId),
  );
  return `Removed '${parsed.relationshipName}' link on ${parsed.entitySetName}(${parsed.id})${parsed.relatedId ? ` to ${parsed.relatedId}` : ""}.`;
}

/** Build the Web API attribute metadata payload for create_dataverse_column. */
export function buildColumnMetadata(parsed: z.infer<typeof createDataverseColumnInputSchema>): Record<string, unknown> {
  const requiredLevel = {
    Value: parsed.required ? "ApplicationRequired" : "None",
    CanBeChanged: true,
    ManagedPropertyLogicalName: "canmodifyrequirementlevelsettings",
  };
  const base: Record<string, unknown> = {
    SchemaName: parsed.schemaName,
    DisplayName: {
      "@odata.type": "Microsoft.Dynamics.CRM.Label",
      LocalizedLabels: [{ "@odata.type": "Microsoft.Dynamics.CRM.LocalizedLabel", Label: parsed.displayName, LanguageCode: 1033 }],
    },
    ...(parsed.description
      ? {
          Description: {
            "@odata.type": "Microsoft.Dynamics.CRM.Label",
            LocalizedLabels: [{ "@odata.type": "Microsoft.Dynamics.CRM.LocalizedLabel", Label: parsed.description, LanguageCode: 1033 }],
          },
        }
      : {}),
    RequiredLevel: requiredLevel,
  };

  const localizedLabel = (text: string): Record<string, unknown> => ({
    "@odata.type": "Microsoft.Dynamics.CRM.Label",
    LocalizedLabels: [{ "@odata.type": "Microsoft.Dynamics.CRM.LocalizedLabel", Label: text, LanguageCode: 1033 }],
  });

  switch (parsed.type) {
    case "string":
      return { ...base, "@odata.type": "Microsoft.Dynamics.CRM.StringAttributeMetadata", MaxLength: parsed.maxLength ?? 100, FormatName: { Value: "Text" } };
    case "memo":
      return { ...base, "@odata.type": "Microsoft.Dynamics.CRM.MemoAttributeMetadata", MaxLength: parsed.maxLength ?? 2000 };
    case "integer":
      return { ...base, "@odata.type": "Microsoft.Dynamics.CRM.IntegerAttributeMetadata", Format: "None" };
    case "decimal":
      return { ...base, "@odata.type": "Microsoft.Dynamics.CRM.DecimalAttributeMetadata", Precision: 2 };
    case "money":
      return { ...base, "@odata.type": "Microsoft.Dynamics.CRM.MoneyAttributeMetadata", Precision: 2 };
    case "datetime":
      return {
        ...base,
        "@odata.type": "Microsoft.Dynamics.CRM.DateTimeAttributeMetadata",
        Format: parsed.dateTimeBehavior === "DateOnly" ? "DateOnly" : "DateAndTime",
        DateTimeBehavior: { Value: parsed.dateTimeBehavior ?? "UserLocal" },
      };
    case "boolean":
      return {
        ...base,
        "@odata.type": "Microsoft.Dynamics.CRM.BooleanAttributeMetadata",
        OptionSet: {
          "@odata.type": "Microsoft.Dynamics.CRM.BooleanOptionSetMetadata",
          TrueOption: { Value: 1, Label: localizedLabel("Yes") },
          FalseOption: { Value: 0, Label: localizedLabel("No") },
        },
      };
    case "picklist":
      return {
        ...base,
        "@odata.type": "Microsoft.Dynamics.CRM.PicklistAttributeMetadata",
        OptionSet: {
          "@odata.type": "Microsoft.Dynamics.CRM.OptionSetMetadata",
          IsGlobal: false,
          OptionSetType: "Picklist",
          Options: (parsed.options ?? []).map((o) => ({ Value: o.value, Label: localizedLabel(o.label) })),
        },
      };
  }
}

export async function handleCreateDataverseColumn(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = createDataverseColumnInputSchema.parse(input);

  if (parsed.type === "picklist" && (!parsed.options || parsed.options.length === 0)) {
    return "A picklist column needs at least one option — pass options: [{value, label}, ...].";
  }
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Not confirmed. Set confirm=true to create ${parsed.type} column '${parsed.schemaName}' on '${parsed.tableLogicalName}'. Schema changes are hard to undo — deleting a column later destroys its data.`);
  }
  logger.info({ table: parsed.tableLogicalName, schemaName: parsed.schemaName }, "Executing create_dataverse_column");

  await withCategory(() => context.api!.createColumn(parsed.tableLogicalName, buildColumnMetadata(parsed)));
  return `Created ${parsed.type} column '${parsed.schemaName}' on '${parsed.tableLogicalName}'.\nRun publish_all_customizations for it to appear in apps.`;
}

export async function handleCreateDataverseLookupColumn(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = createDataverseLookupColumnInputSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Not confirmed. Set confirm=true to create lookup column '${parsed.schemaName}' on '${parsed.tableLogicalName}' pointing at '${parsed.targetTableLogicalName}'.`);
  }
  logger.info(
    { table: parsed.tableLogicalName, target: parsed.targetTableLogicalName },
    "Executing create_dataverse_lookup_column",
  );

  const localizedLabel = (text: string): Record<string, unknown> => ({
    "@odata.type": "Microsoft.Dynamics.CRM.Label",
    LocalizedLabels: [{ "@odata.type": "Microsoft.Dynamics.CRM.LocalizedLabel", Label: text, LanguageCode: 1033 }],
  });

  await withCategory(() =>
    context.api!.createLookupColumn({
      "@odata.type": "Microsoft.Dynamics.CRM.OneToManyRelationshipMetadata",
      SchemaName: parsed.relationshipSchemaName ?? `${parsed.schemaName}_relationship`,
      ReferencedEntity: parsed.targetTableLogicalName,
      ReferencingEntity: parsed.tableLogicalName,
      Lookup: {
        SchemaName: parsed.schemaName,
        DisplayName: localizedLabel(parsed.displayName),
        AttributeType: "Lookup",
        AttributeTypeName: { Value: "LookupType" },
      },
    }),
  );
  return `Created lookup column '${parsed.schemaName}' on '${parsed.tableLogicalName}' → '${parsed.targetTableLogicalName}'.\nRun publish_all_customizations for it to appear in apps.`;
}

export async function handleBatchDataverseOperations(
  context: DataverseDepthToolsContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return notAvailable();
  const parsed = batchDataverseOperationsInputSchema.parse(input);

  const hasDelete = parsed.operations.some((op) => op.method === "DELETE");
  if (hasDelete && !parsed.confirm) {
    return "Not confirmed. This batch contains DELETE operations — set confirm=true to run it. Deletions cannot be undone.";
  }
  if (parsed.atomic && parsed.operations.some((op) => op.method === "GET")) {
    return "Atomic batches (changesets) cannot contain GET operations — run reads separately or set atomic=false.";
  }
  for (const op of parsed.operations) {
    if ((op.method === "PATCH" || op.method === "DELETE") && !op.id) {
      return `A ${op.method} operation on ${op.entitySetName} needs an id.`;
    }
    if ((op.method === "POST" || op.method === "PATCH") && !op.data) {
      return `A ${op.method} operation on ${op.entitySetName} needs data.`;
    }
  }
  logger.info({ count: parsed.operations.length, atomic: parsed.atomic }, "Executing batch_dataverse_operations");

  const results = await withCategory(() =>
    context.api!.executeBatch(
      parsed.operations.map((op) => {
        const path = op.id ? `${op.entitySetName}(${op.id})` : op.entitySetName;
        const built: { method: "GET" | "POST" | "PATCH" | "DELETE"; path: string; data?: Record<string, unknown> } = {
          method: op.method,
          path,
        };
        if (op.data !== undefined) built.data = op.data;
        return built;
      }),
      parsed.atomic ?? false,
    ),
  );

  const failed = results.filter((r) => r.status >= 400);
  const lines = [
    `Batch complete: ${results.length} responses, ${results.length - failed.length} succeeded, ${failed.length} failed${parsed.atomic ? " (atomic)" : ""}.`,
  ];
  results.forEach((r, i) => {
    lines.push(`  ${i + 1}. HTTP ${r.status}${r.status >= 400 && r.body ? ` — ${r.body.slice(0, 200)}` : ""}`);
  });
  if (parsed.atomic && failed.length > 0) {
    lines.push("\nAtomic changeset: ALL operations were rolled back because at least one failed.");
  }
  return lines.join("\n");
}
